// Dependencies
import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import flatpickr from "flatpickr";
import rangePlugin from "flatpickr/dist/plugins/rangePlugin";
import { Portuguese } from "flatpickr/dist/l10n/pt.js";
import { Instance } from "flatpickr/dist/types/instance";

// Styles
import { ButtonWrapper, Container } from "./styles";

// Assets
import alertIcon from "./assets/alert-icon.svg";

// Components
import { WebpButton } from "components/WebpButton";

// Utils
import { formatDefault } from "./utils/dateFormat";

// Types
type WebpCalendarV2Props = {
    labelAlert: string;
    currentDate: string;
    labelButtonAlert: string;

    setMessageAlert: boolean;
    clearFullCalendar: boolean;

    onClearCalendar: (calendar: any) => void;
    onClickDateFinalCalendar: (ev: any) => void;
    onClickDateInitialCalendar: (ev: any) => void;
};

export const WebpCalendarV2: FunctionComponent<WebpCalendarV2Props> = ({
    labelAlert,
    currentDate,
    labelButtonAlert,
    setMessageAlert = false,
    clearFullCalendar,
    onClearCalendar,
    onClickDateFinalCalendar,
    onClickDateInitialCalendar
}) => {
    const flatPickrRef1 = useRef<HTMLDivElement>(null);
    const flatPickrRefInput1 = useRef<HTMLInputElement>(null);
    const flatPickrRefInput2 = useRef<HTMLInputElement>(null);

    const [showButtonClear, setShowButtonClear] = useState<boolean>(false);
    const [showDefaultDate, setShowDefaultDate] = useState<boolean>(true);
    const [initialDate, setInitialDate] = useState<string>();
    const [finalDate, setFinalDate] = useState<string>();

    const windowWidth = window.innerWidth;

    const clearFinalDate = useCallback(() => {
        setInitialDate(undefined);

        setFinalDate(undefined);

        flatPickrRefInput1.current?.focus();

        if (flatPickrRefInput1.current) {
            flatPickrRefInput1.current.value = "";
        }

        if (flatPickrRefInput2.current) {
            flatPickrRefInput2.current.value = "";
        }

        setShowButtonClear(false);

        onClearCalendar(flatPickrRefInput1.current?.value || "");
    }, [onClearCalendar]);

    useEffect(() => {
        const handleClickedInitialCalendar = (ev: any) => {
            setShowButtonClear(true);

            setShowDefaultDate(false);

            onClickDateInitialCalendar(ev);
        };

        const handleClickedFinalCalendar = (ev: any) => {
            onClickDateFinalCalendar(ev);
        };

        if (flatPickrRefInput1.current && flatPickrRefInput2.current && flatPickrRef1.current) {
            const calendar: Instance = flatpickr(flatPickrRefInput1.current, {
                mode: "range",
                inline: true,
                showMonths: windowWidth < 768 ? 1 : 2,
                appendTo: flatPickrRef1.current,
                minDate: currentDate || "today",
                dateFormat: "d/m/Y",
                monthSelectorType: "static",
                locale: Portuguese,
                defaultDate: showDefaultDate && initialDate && finalDate ? [initialDate, finalDate] : undefined,
                plugins: [rangePlugin({ input: flatPickrRefInput2.current })],
                onChange: (selectedDates: Date[]) => {
                    const dateArr = selectedDates.map((date) => formatDefault(date));

                    if (dateArr[0]) {
                        handleClickedInitialCalendar(dateArr[0]);
                    }

                    if (dateArr[1]) {
                        handleClickedFinalCalendar(dateArr[1]);

                        if (flatPickrRefInput2.current) {
                            flatPickrRefInput2.current.focus();
                        }
                    }
                }
            });

            const clearFullDate = () => {
                if (!!clearFullCalendar) {
                    clearFinalDate();
                }
            };

            clearFullDate();

            return () => {
                calendar.destroy();
            };
        }
    }, [
        windowWidth,
        currentDate,
        initialDate,
        finalDate,
        showDefaultDate,
        onClickDateInitialCalendar,
        onClickDateFinalCalendar,
        clearFullCalendar,
        onClearCalendar,
        clearFinalDate
    ]);

    return (
        <Container>
            <div className="content-calendars">
                <div className="flatpicker-wrapper" ref={flatPickrRef1}>
                    <div className="flatpicker">
                        <div className="label-input">
                            <label className="label">Início</label>

                            <input id="input1" data-input placeholder="  /  /    " className="input" type="text" ref={flatPickrRefInput1} />
                        </div>

                        <div className="divider" />

                        <div className="label-input">
                            <div>
                                <label className="label">Término</label>

                                <input id="input2" data-input placeholder="  /  /    " className="input" type="text" ref={flatPickrRefInput2} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showButtonClear && (
                <div className="wrapper-alert">
                    {setMessageAlert && (
                        <div className="content-alert">
                            <img className="img" alt="alert icon" src={alertIcon} />

                            <p className="text">{labelAlert}</p>
                        </div>
                    )}

                    <ButtonWrapper>
                        <WebpButton variant="outline-warning" contentButtonComposition={labelButtonAlert} onClick={clearFinalDate} />
                    </ButtonWrapper>
                </div>
            )}
        </Container>
    );
};
